import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  Tooltip,
  XAxis,
  YAxis,
  ResponsiveContainer,
  TooltipContentProps,
  LegendProps,
  DefaultLegendContentProps,
  DefaultLegendContent,
} from 'recharts';
import { LineConfig, TransformedDataPoint } from './helpers/prepareChartData';
import { useTranslation } from 'react-i18next';

// #endregion
const CrmChartsComponent = ({
  data,
  lines,
}: {
  data: TransformedDataPoint[] | null;
  lines: LineConfig[] | null;
}) => {
  const { t } = useTranslation();
  if (!data || !lines) {
    return <div style={{ textAlign: 'center', padding: '20px' }}>{t('global.noTableData')}</div>;
  }
  return (
    <div
      style={{
        width: '100%',
        maxWidth: '1400px',
        maxHeight: '70vh',
        position: 'relative',
        overflow: 'visible',
      }}
    >
      <ResponsiveContainer width="100%" height={550}>
        <LineChart
          data={data}
          margin={{
            top: 15,
            right: 20,
            left: 0,
            bottom: 60,
          }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#2b5f6066" />
          <XAxis
            dataKey="name"
            stroke="#2b5f60bc"
            interval={0}
            tickMargin={40}
            padding={{ left: 20, right: 20 }}
            angle={-60}
          />
          <YAxis yAxisId="left" width="auto" stroke="#ce1c1c94" />

          <Tooltip content={(props) => <CustomTooltip {...(props as any)} lines={lines} />} />
          <Legend
            verticalAlign="bottom"
            height={36}
            wrapperStyle={{ bottom: 0, zIndex: 1 }}
            content={(props) => <CustomLegend {...(props as any)} />}
          />
          {lines.map((line) => (
            <Line
              key={line.key}
              yAxisId="left"
              type="monotone"
              dataKey={line.key}
              name={line.name}
              stroke={line.color}
              dot={{
                fill: 'var(--color-surface-base)',
              }}
              activeDot={{ r: 8, stroke: 'var(--color-surface-base)' }}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};
export default CrmChartsComponent;

const CustomTooltip = ({
  active,
  payload,
  label,
  ...rest
}: TooltipContentProps<any, any> & { lines: LineConfig[] }) => {
  const { t } = useTranslation();
  const lines = (rest as any).lines as LineConfig[];
  const coordinate = (rest as any).coordinate as { x?: number; y?: number } | undefined;

  const isVisible = !!(
    active &&
    payload &&
    payload.length &&
    coordinate &&
    typeof coordinate.x === 'number'
  );
  if (!isVisible) {
    return <div className="custom-tooltip" style={{ visibility: 'hidden' }} />;
  }

  return (
    <div
      className="custom-tooltip"
      style={{
        visibility: 'visible',
        padding: 8,
        backgroundColor: '#fff',
        border: '1px solid #2b5f60',
        borderRadius: 8,
        color: '#000000eb',
        zIndex: 5,
        opacity: 1,

        pointerEvents: 'none',
        whiteSpace: 'nowrap',
      }}
    >
      <p
        className="label"
        style={{
          fontWeight: 600,
          color: '#000',
          fontSize: 14,
          textTransform: 'none',
          marginBottom: '8px',
        }}
      >
        {t('control.crmCharts.chartsTooltip.violationDate', { date: label })}
      </p>
      {payload!.map((pItem, idx) => {
        const dataKey = (pItem as any).dataKey || (pItem as any).name;
        const line = lines.find((l) => l.key === dataKey) as LineConfig | undefined;

        // prefer per-date reasons stored on the data point payload
        const dataPoint = (pItem as any).payload as Record<string, any> | undefined;
        const reasons: { reason: string; count: number }[] | undefined =
          dataPoint?.[`${dataKey}_reasons`] ?? line?.reasons;

        return (
          <div key={idx} style={{ marginBottom: 6, color: line ? line.color : '#000' }}>
            <div style={{ fontWeight: 600, textTransform: 'none' }}>
              {line
                ? t(`global.selectorStatus.${pItem.name}`, { defaultValue: pItem.name })
                : pItem.name}
              : {(pItem as any).value}
            </div>
            {reasons && reasons.length > 0 && (() => {
              const singleSameAsLine =
                reasons.length === 1 && line && (reasons[0].reason === line.name || reasons[0].reason === (pItem as any).name);
              if (singleSameAsLine) return null;
              return (
                <ul style={{ margin: '4px 0 0 12px', padding: '0 0 0 16px' }}>
                  {reasons.map((r, i) => (
                    <li key={i} style={{ listStyle: 'disc', fontSize: 12, textTransform: 'none' }}>
                      {t(`control.crmTable.reason.${r.reason}`, { defaultValue: r.reason })}: {r.count}
                    </li>
                  ))}
                </ul>
              );
            })()}
          </div>
        );
      })}
    </div>
  );
};

const CustomLegend = ({ payload, ref, ...rest }: DefaultLegendContentProps) => {
  const { t } = useTranslation();
  const newPayload = payload?.map((entry) => ({
    ...entry,
    value: t(`global.selectorStatus.${entry.value}`, { defaultValue: entry.value }),
  }));

  return <DefaultLegendContent payload={newPayload} {...rest} />;
};
